/** **Revoke link now…**: an anchored confirm (`role="alertdialog"`, focus on Cancel) —
 *  "It stops working at once. Anyone who opens it or scans its QR code sees 'withdrawn'."
 *  — with the lines that apply to this link: its registrations keep their bookings; the
 *  master needs a successor (D5: one radio per active custom link without a date, or
 *  "Create a new master link"; **Revoke and switch master now**); the last tour-wide link
 *  while self-registration is on.
 *
 *  A failure stays in the confirm with Retry: `successor_required` (the link became the
 *  master meanwhile: the list is re-read, so the choice appears), `invalid_successor`
 *  (pick again), `master_conflict` (409: nothing was written). On success the caller shows
 *  the snackbar with Undo. */
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { Box, Button, FormControlLabel, Popover, Radio, RadioGroup, Typography } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useId, useState } from 'react';

import * as toursApi from '@/api/tours';
import type { TourSelfregLink, TourSelfregRevokeResult } from '@/api/tours';
import { ImmediateActionButton } from '@/features/records/actions/ImmediateActionButton';

import { addressTail, createdWords, registeredWords, successorsOf } from './linkWords';
import { errorCode, errorText } from './queries';

export const NEW_MASTER = 'new';

export function RevokeConfirm({
  tourId,
  link,
  anchorEl,
  tourWideLinks,
  selfregOn,
  onClose,
  onRevoked,
  onStale,
}: {
  tourId: number;
  /** The link as the list shows it now (re-read after a failure). */
  link: TourSelfregLink | null;
  anchorEl: HTMLElement | null;
  /** The tour-wide list's rows (the successors, "the last tour-wide link"). */
  tourWideLinks: readonly TourSelfregLink[];
  /** The stored tour allows self-registration. */
  selfregOn: boolean;
  onClose: () => void;
  onRevoked: (link: TourSelfregLink, result: TourSelfregRevokeResult) => void;
  /** Re-read the lists (the server says ours is out of date). */
  onStale: () => void;
}) {
  const open = Boolean(link && anchorEl);
  const titleId = useId();
  const bodyId = useId();
  const [successor, setSuccessor] = useState<string | null>(null);

  const isMaster = Boolean(link?.is_master);
  const successors = link && isMaster ? successorsOf(link, tourWideLinks) : [];
  // With no custom link to take over, "Create a new master link" is the only choice.
  const choice = isMaster ? (successors.length === 0 ? NEW_MASTER : successor) : null;

  const revoke = useMutation({
    mutationFn: ({ link: l, successor: to }: { link: TourSelfregLink; successor: string | null }) =>
      toursApi.revokeSelfregLink(tourId, l.id, to ? { successor: to } : {}),
    onSuccess: (r, { link: l }) => onRevoked(l, r),
    onError: (e) => {
      const code = errorCode(e);
      if (code === 'successor_required' || code === 'invalid_successor' || code === 'master_conflict') onStale();
      if (code === 'invalid_successor') setSuccessor(null);
    },
  });

  // A new link (or a reopened confirm) starts clean.
  const linkId = link?.id ?? null;
  useEffect(() => {
    setSuccessor(null);
    revoke.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkId]);

  if (!link) return null;

  const activeTourWide = tourWideLinks.filter((l) => !l.revoked_ts && l.date === null);
  const lastTourWide = !isMaster && link.date === null && activeTourWide.length === 1 && activeTourWide[0]?.id === link.id;
  const chosen = successors.find((s) => s.id === choice);
  const code = revoke.isError ? errorCode(revoke.error) : null;
  const error = !revoke.isError
    ? null
    : code === 'successor_required'
      ? 'This link became the master meanwhile — choose which link takes over.'
      : code === 'invalid_successor'
        ? 'That link can no longer take over — choose again.'
        : code === 'master_conflict'
          ? 'The tour’s master link changed meanwhile — nothing was revoked.'
          : errorText(revoke.error, 'Could not revoke the link.');
  const canRetry = revoke.isError && code !== 'successor_required' && code !== 'invalid_successor';

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={() => {
        if (!revoke.isPending) onClose();
      }}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      slotProps={{
        paper: {
          role: 'alertdialog',
          'aria-labelledby': titleId,
          'aria-describedby': bodyId,
          'data-selfreg-revoke': '',
          sx: { p: 1.5, width: 360, maxWidth: '90vw', display: 'grid', gap: 1 },
        } as object,
      }}
    >
      <Typography id={titleId} variant="subtitle2" component="h2">
        Revoke link {addressTail(link)} now?
      </Typography>
      <Box id={bodyId} sx={{ display: 'grid', gap: 0.75 }}>
        <Typography variant="body2">
          It stops working at once. Anyone who opens it or scans its QR code sees “withdrawn”.
        </Typography>
        {link.registered_count > 0 && (
          <Typography variant="body2" data-revoke-line="registrations">
            Its {link.registered_count === 1 ? 'registration keeps its booking' : `${link.registered_count} registrations keep their bookings`}.
          </Typography>
        )}
        {isMaster && (
          <Typography variant="body2" data-revoke-line="master">
            {chosen
              ? `It is the master link: notifications use it. Link ${addressTail(chosen)} takes over.`
              : choice === NEW_MASTER
                ? 'It is the master link: notifications use it. A new master link will be created.'
                : 'It is the master link: notifications use it. Choose which link takes over.'}
          </Typography>
        )}
        {lastTourWide && selfregOn && (
          <Typography variant="body2" data-revoke-line="last">
            It is the tour’s last tour-wide link, and self-registration is on: guests can join only through a new link.
          </Typography>
        )}
      </Box>
      {isMaster && (
        <RadioGroup
          aria-label="The new master link"
          value={choice ?? ''}
          onChange={(e) => setSuccessor(e.target.value)}
          sx={{ gap: 0.25 }}
        >
          {successors.map((s) => (
            <FormControlLabel
              key={s.id}
              value={s.id}
              control={<Radio size="small" />}
              label={
                <Box sx={{ display: 'grid' }}>
                  <Typography variant="body2">Make link {addressTail(s)} the master</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Created {createdWords(s.created_ts).text} · {registeredWords(s.registered_count)}
                  </Typography>
                </Box>
              }
            />
          ))}
          <FormControlLabel
            value={NEW_MASTER}
            control={<Radio size="small" />}
            label={
              <Box sx={{ display: 'grid' }}>
                <Typography variant="body2">Create a new master link</Typography>
                <Typography variant="caption" color="text.secondary">
                  With the defaults: no verification, the operator’s guide role.
                </Typography>
              </Box>
            }
          />
        </RadioGroup>
      )}
      {error && (
        <Typography variant="caption" color="error" role="alert" sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }} data-revoke-error>
          <ErrorOutlineIcon aria-hidden sx={{ fontSize: 16 }} />
          {error}
          {canRetry && (
            <Button size="small" color="inherit" onClick={() => revoke.mutate({ link, successor: choice })} sx={{ minWidth: 0, py: 0, ml: 'auto' }}>
              Retry
            </Button>
          )}
        </Typography>
      )}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
        {/* Focus on Cancel: a destructive confirm never starts on its action. */}
        <Button autoFocus size="small" onClick={onClose} disabled={revoke.isPending}>
          Cancel
        </Button>
        <ImmediateActionButton
          variant="contained"
          color="error"
          disabled={revoke.isPending || (isMaster && !choice)}
          onClick={() => revoke.mutate({ link, successor: choice })}
        >
          {revoke.isPending ? 'Revoking…' : isMaster ? 'Revoke and switch master now' : 'Revoke link now'}
        </ImmediateActionButton>
      </Box>
    </Popover>
  );
}
